import React, { useState, useEffect } from 'react';
import { uploadImage, STORAGE_PATHS } from '../../services/firebase-storage.js';
import { API } from '../../utils/database-frontend.js';

const GalleryForm = ({ onSuccess, onError }) => {
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [showNewCategoryInput, setShowNewCategoryInput] = useState(false);
  const [formData, setFormData] = useState({
    altText: '',
    category: '',
    newCategory: '',
    isFeatured: false,
    image: null
  });

  const defaultCategories = [
    { value: 'projeler', label: 'Projeler' },
    { value: 'makineler', label: 'Makineler' },
    { value: 'ekip', label: 'Ekip' },
    { value: 'genel', label: 'Genel' }
  ];

  // Load existing categories
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const existingCategories = await API.gallery.getCategories();
        
        // Merge default categories with existing ones
        const allCategories = [...defaultCategories];
        
        existingCategories.forEach(cat => {
          if (!defaultCategories.find(def => def.value === cat)) {
            allCategories.push({ value: cat, label: cat.charAt(0).toUpperCase() + cat.slice(1) });
          }
        });
        
        setCategories(allCategories);
        
        // Set default category if none selected
        if (!formData.category && allCategories.length > 0) {
          setFormData(prev => ({ ...prev, category: allCategories[0].value }));
        }
      } catch (error) {
        // If API fails, use default categories
        setCategories(defaultCategories);
        setFormData(prev => ({ ...prev, category: 'projeler' }));
        console.warn('Kategoriler yüklenemedi, varsayılan kategoriler kullanılıyor');
      }
    };

    loadCategories();
  }, []);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name === 'category' && value === 'new') {
      setShowNewCategoryInput(true);
      setFormData(prev => ({
        ...prev,
        category: 'new'
      }));
    } else if (name === 'category' && value !== 'new') {
      setShowNewCategoryInput(false);
      setFormData(prev => ({
        ...prev,
        [name]: value,
        newCategory: ''
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  const handleFileChange = (e) => {
    setFormData(prev => ({
      ...prev,
      image: e.target.files[0]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.image) {
      onError('❌ Lütfen bir görsel seçin');
      return;
    }

    setLoading(true);
    
    try {
      // Upload image to Firebase Storage
      const uploadResult = await uploadImage(
        formData.image,
        STORAGE_PATHS.GALLERY,
        `gallery-${Date.now()}.${formData.image.name.split('.').pop()}`
      );

      // Determine category to use
      const categoryToUse = formData.category === 'new' ? formData.newCategory.trim() : formData.category;
      
      if (!categoryToUse) {
        onError('❌ Lütfen bir kategori seçin veya yeni kategori girin');
        setLoading(false);
        return;
      }

      // Add to PostgreSQL database
      const galleryData = {
        ImageUrl: uploadResult.url,
        FirebaseImagePath: uploadResult.path,
        AltText: formData.altText || 'Galeri görseli',
        Category: categoryToUse,
        IsFeatured: formData.isFeatured
      };

      await API.gallery.create(galleryData);
      
      onSuccess('✅ Galeri görseli başarıyla eklendi!');
      
      // Reset form
      setFormData({
        altText: '',
        category: categories.length > 0 ? categories[0].value : '',
        newCategory: '',
        isFeatured: false,
        image: null
      });
      setShowNewCategoryInput(false);
      
      // Reset file input
      const fileInput = document.querySelector('input[type="file"]');
      if (fileInput) fileInput.value = '';
      
    } catch (error) {
      onError('❌ Galeri görseli eklenirken hata oluştu: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <h2 className="text-lg font-semibold mb-4">🖼️ Yeni Galeri Görseli Ekle</h2>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Kategori
          </label>
          <select
            name="category"
            value={formData.category}
            onChange={handleInputChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            required
          >
            <option value="">Kategori seçin</option>
            {categories.map((cat) => (
              <option key={cat.value} value={cat.value}>
                {cat.label}
              </option>
            ))}
            <option value="new">➕ Yeni Kategori Ekle</option>
          </select>
        </div>

        {showNewCategoryInput && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Yeni Kategori Adı
            </label>
            <input
              type="text"
              name="newCategory"
              value={formData.newCategory}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              placeholder="Örn: Yeni Projeler, Özel Makineler..."
              required
            />
            <p className="text-xs text-gray-500 mt-1">
              Bu yeni kategori sisteme eklenecek ve gelecekte kullanılabilir.
            </p>
          </div>
        )}
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Görsel Açıklaması (Opsiyonel)
          </label>
          <input
            type="text"
            name="altText"
            value={formData.altText}
            onChange={handleInputChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            placeholder="Örn: Beton pompa çalışması"
          />
        </div>

        <div className="flex items-center">
          <input
            type="checkbox"
            name="isFeatured"
            id="galleryFeatured"
            checked={formData.isFeatured}
            onChange={handleInputChange}
            className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
          />
          <label htmlFor="galleryFeatured" className="ml-2 block text-sm text-gray-700">
            Öne Çıkan Galeri Görseli
          </label>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Galeri Görseli
          </label>
          <input
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            required
          />
          {formData.image && (
            <p className="mt-2 text-sm text-gray-600">
              Seçilen dosya: {formData.image.name}
            </p>
          )}
        </div>
        
        <button
          type="submit"
          disabled={loading}
          className="w-full bg-indigo-600 text-white py-2 px-4 rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition duration-300"
        >
          {loading ? '⏳ Ekleniyor...' : '✅ Galeri Ekle'}
        </button>
      </form>
    </div>
  );
};

export default GalleryForm; 